
import Vue, { PropType } from "vue";

class Selector {
  public id!: string;
  public label!: string;

  public constructor(init?: Partial<Selector>) {
    Object.assign(this, init);
  }
}
export default Vue.extend({
  name: "SingleSelect",
  props: {
    options: {
      type: Array as PropType<Selector[]>,
      default: () => [],
    },
    title: { type: String, default: "" },
    name: { type: String, default: "" },
    errors: { type: Boolean, default: false },
    warning: { type: Boolean, default: false },
    // BIK-2107: green "verified" border, mirrors the warning/error styling.
    verified: { type: Boolean, default: false },
    selection: { type: [Boolean, Object], default: false },
    arialabel: { type: String, default: "" },
    customH: { type: String, default: "" },
    allowEmpty: { type: Boolean, default: false },
    diffKey: { type: String, default: "" },
    disabled: {
      type: Boolean,
      default: false,
    },
    useId: {
      type: Boolean,
      default: false,
    },
    useLabelCombination: {
      type: Boolean,
      default: false,
    },
    // BIK-2125: opt-in typeahead — swaps the click-only button for a text input the
    // user can type into to filter options. Default false preserves legacy behavior
    // for every existing SingleSelect usage (Country, protection plans, etc.).
    searchable: {
      type: Boolean,
      default: false,
    },
    // BIK-2125: id/name for the searchable input. Browser/password-manager autofill
    // heuristics key off id/name (not just autocomplete) to decide whether a field
    // belongs to the address group being filled — without it the browser can skip
    // the field entirely even with autocomplete="address-level1" set.
    inputId: { type: String, default: "" },
  },
  data() {
    return {
      isOpen: false,
      selected: {} as Selector,
      show: false,
      // BIK-2125: local typed text while searchable; mirrors selected.label when idle.
      filterText: "",
    };
  },
  computed: {
    // BIK-2125: options filtered by the typed text (matches label or id, e.g. "CA").
    // Untouched (all options) when not searchable or nothing typed yet.
    visibleOptions(): Selector[] {
      if (!this.searchable || !this.filterText) return this.options;
      const needle = this.filterText.toLowerCase();
      return this.options.filter(
        (item) =>
          item.label?.toLowerCase().includes(needle) ||
          item.id?.toLowerCase().includes(needle)
      );
    },
  },
  watch: {
    selection(newVal) {
      if (newVal) {
        this.select(newVal);
      }
    },
  },
  mounted() {
    if (this.options) {
      if (this.allowEmpty) {
        this.show = true;
        this.selected = { id: "", label: "-" };
      } else {
        this.selected = this.options[0];
      }
    }
    if (this.selection) {
      this.select(this.selection);
    }
    this.filterText = this.useId ? this.selected?.id : this.selected?.label;
  },
  methods: {
    onShow() {
      this.isOpen = !this.isOpen;
    },
    close() {
      this.isOpen = false;
    },
    // BIK-2125: the hidden native <select> was filled by browser/password-manager
    // address autofill (matched by option value = item.id). Route it through the
    // normal select() so the visible input, filterText, and the selectionMade
    // emit all update exactly as a manual pick would.
    onNativeSelectAutofill($event: Event): void {
      const id = ($event.target as HTMLSelectElement)?.value;
      if (!id) return;
      const match = this.options.find((item) => item.id === id);
      if (match) {
        this.select(match);
      }
    },
    // BIK-2125: an exact (case-insensitive) match against an option's label or
    // id/code — e.g. typing "Texas" or "TX" in full, or the browser autofill
    // dropping "TX" into the field in one shot without going through select().
    // Autofill fires a native input event but never calls select() itself, so
    // without this the value would just sit in filterText and get wiped on blur.
    findExactMatch(): Selector | undefined {
      if (!this.filterText) return undefined;
      const needle = this.filterText.toLowerCase();
      return this.options.find(
        (item) =>
          item.label?.toLowerCase() === needle ||
          item.id?.toLowerCase() === needle
      );
    },
    // BIK-2125: keep the dropdown reactive to typed text; matching options are
    // computed via visibleOptions. Auto-select on an exact match so browser/
    // password-manager autofill (which sets the value without picking from the
    // list) still lands a real selection instead of stray unmatched text.
    onFilterInput() {
      if (!this.isOpen) this.isOpen = true;
      const match = (this as any).findExactMatch();
      if (match) {
        this.select(match);
      }
    },
    // BIK-2125: leaving the field without picking a matching option snaps the
    // displayed text back to the last valid selection — never leaves free text
    // that doesn't correspond to a real option (would send an unrecognized
    // state to AVS). @mousedown.prevent on option rows lets a click-to-select
    // land before this blur fires. An exact match typed right before blur
    // (no time for the input handler to run, e.g. fast autofill) still selects.
    onBlur() {
      this.isOpen = false;
      const match = (this as any).findExactMatch();
      if (match) {
        this.select(match);
        return;
      }
      this.filterText = this.useId ? this.selected?.id : this.selected?.label;
    },
    select(item: Selector) {
      this.selected = item;
      this.show = true;
      this.isOpen = false;
      this.filterText = this.useId ? item?.id : item?.label;
      this.$emit("selectedItem", item);
      if (this.name || item) {
        this.$emit("selectionMade", {
          option: item,
          name: this.name ? this.name : this.title,
        });
      }
    },
  },
});
